import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

export const useVideoTabs = (editCommunityId) => {
  const [videoTabs, setVideoTabs] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [videoCoverFiles, setVideoCoverFiles] = useState([]);

  // Tab state
  const [currentTab, setCurrentTab] = useState({ tab_name: "", sort_order: 0 });
  const [editTabIndex, setEditTabIndex] = useState(-1);

  // Group state
  const [currentGroup, setCurrentGroup] = useState({
    group_title: "",
    group_description: "",
    sort_order: 0,
    group_banner_mobile_file: null,
    group_banner_mobile_url: null,
    group_banner_web_file: null,
    group_banner_web_url: null,
  });
  const [editGroupIndex, setEditGroupIndex] = useState(-1);

  // Video state
  const [currentVideo, setCurrentVideo] = useState({
    title: "",
    url: "",
    video_type: "youtube",
    membership_ids: [],
    sort_order: 0,
    cover_image: null,
    cover_image_file: null,
    cover_image_index: null,
    membership_expiry: {},
    attachment_file: null,
    attachment_file_url: null,
  });
  const [editVideoIndex, setEditVideoIndex] = useState(-1);
  const [isVideoFree, setIsVideoFree] = useState(true);

  const resetTabForm = () => {
    setCurrentTab({ tab_name: "", sort_order: 0 });
    setEditTabIndex(-1);
  };

  const resetGroupForm = () => {
    setCurrentGroup({
      group_title: "",
      group_description: "",
      sort_order: 0,
      group_banner_mobile_file: null,
      group_banner_mobile_url: null,
      group_banner_web_file: null,
      group_banner_web_url: null,
    });
    setEditGroupIndex(-1);
  };

  const resetVideoForm = () => {
    setCurrentVideo({
      title: "",
      url: "",
      video_type: "youtube",
      membership_ids: [],
      sort_order: 0,
      cover_image: null,
      cover_image_file: null,
      cover_image_index: null,
      membership_expiry: {},
      attachment_file: null,
      attachment_file_url: null,
    });
    setIsVideoFree(true);
    setEditVideoIndex(-1);
  };

  // ─── MEMBERSHIP EXPIRY HELPERS ───────────────────────────────────────────

  const setMembershipExpiry = (membershipId, isoString) => {
    setCurrentVideo((prev) => ({
      ...prev,
      membership_expiry: {
        ...prev.membership_expiry,
        [membershipId]: isoString,
      },
    }));
  };

  const clearMembershipExpiry = (membershipId) => {
    setCurrentVideo((prev) => {
      const updated = { ...prev.membership_expiry };
      delete updated[membershipId];
      return { ...prev, membership_expiry: updated };
    });
  };

  const buildMembershipExpiryPayload = (
    isVideoFree,
    membershipIds,
    membershipExpiryMap,
  ) => {
    if (isVideoFree) {
      const expiresAt = membershipExpiryMap[0] ?? null;
      return [{ membership_id: 0, expires_at: expiresAt }];
    } else {
      return (membershipIds || []).map((id) => ({
        membership_id: Number(id),
        expires_at: membershipExpiryMap[id] ?? null,
      }));
    }
  };

  // ─── TAB MANAGEMENT ──────────────────────────────────────────────────────

  const handleAddOrUpdateTab = () => {
    if (!currentTab.tab_name.trim()) {
      toast.error("Please enter a tab name");
      return;
    }

    const payload = {
      tab_name: currentTab.tab_name.trim(),
      sort_order:
        editTabIndex === -1 ? videoTabs.length : currentTab.sort_order,
      groups: editTabIndex === -1 ? [] : videoTabs[editTabIndex].groups || [],
    };

    if (editTabIndex === -1) {
      setVideoTabs((prev) => [...prev, payload]);
      setActiveTabIndex(videoTabs.length);
    } else {
      setVideoTabs((prev) =>
        prev.map((t, i) => (i === editTabIndex ? { ...t, ...payload } : t)),
      );
    }

    resetTabForm();
  };

  const handleEditTab = (index) => {
    setCurrentTab({
      tab_name: videoTabs[index].tab_name,
      sort_order: videoTabs[index].sort_order || index,
    });
    setEditTabIndex(index);
  };

  const handleDeleteTab = (index) => {
    confirmAlert({
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this tab? All groups and videos in this tab will be deleted.",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            setVideoTabs((prev) => {
              const updated = prev.filter((_, i) => i !== index);
              return updated.map((tab, i) => ({ ...tab, sort_order: i }));
            });
            if (activeTabIndex >= videoTabs.length - 1) {
              setActiveTabIndex(Math.max(0, videoTabs.length - 2));
            }
            resetGroupForm();
            resetVideoForm();
            toast.success("Tab deleted");
          },
        },
        { label: "No" },
      ],
    });
  };

  const handleMoveTabUp = (index) => {
    if (index === 0) return;
    setVideoTabs((prev) => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [
        newList[index],
        newList[index - 1],
      ];
      return newList.map((tab, i) => ({ ...tab, sort_order: i }));
    });
  };

  const handleMoveTabDown = (index) => {
    if (index === videoTabs.length - 1) return;
    setVideoTabs((prev) => {
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [
        newList[index + 1],
        newList[index],
      ];
      return newList.map((tab, i) => ({ ...tab, sort_order: i }));
    });
  };

  // ─── GROUP MANAGEMENT ────────────────────────────────────────────────────

  const handleGroupBannerWebUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCurrentGroup((prev) => ({
      ...prev,
      group_banner_web_file: file,
      group_banner_web_url: URL.createObjectURL(file),
    }));
  };

  const handleGroupBannerMobileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCurrentGroup((prev) => ({
      ...prev,
      group_banner_mobile_file: file,
      group_banner_mobile_url: URL.createObjectURL(file),
    }));
  };

  const handleAddOrUpdateGroup = () => {
    if (!currentGroup.group_title.trim()) {
      toast.error("Please enter a group title");
      return;
    }
    if (videoTabs.length === 0) {
      toast.error("Please create a tab first");
      return;
    }

    let groupMobileUrl = null;
    let groupMobileFile = null;
    let groupWebUrl = null;
    let groupWebFile = null;

    if (editGroupIndex === -1) {
      groupMobileFile = currentGroup.group_banner_mobile_file || null;
      groupWebFile = currentGroup.group_banner_web_file || null;
    } else {
      if (currentGroup.group_banner_mobile_file) {
        groupMobileFile = currentGroup.group_banner_mobile_file;
      } else {
        groupMobileUrl = currentGroup.group_banner_mobile_url || null;
      }
      if (currentGroup.group_banner_web_file) {
        groupWebFile = currentGroup.group_banner_web_file;
      } else {
        groupWebUrl = currentGroup.group_banner_web_url || null;
      }
    }

    // Capture these outside for use after setVideoTabs
    const capturedEditGroupIndex = editGroupIndex;
    const capturedActiveTabIndex = activeTabIndex;

    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== capturedActiveTabIndex) return tab;
        const groups = tab.groups || [];

        const payload = {
          group_title: currentGroup.group_title.trim(),
          group_description: currentGroup.group_description?.trim() || "",
          sort_order:
            capturedEditGroupIndex === -1
              ? groups.length
              : currentGroup.sort_order,
          videos:
            capturedEditGroupIndex === -1
              ? []
              : groups[capturedEditGroupIndex]?.videos || [], // ← fresh from prev
          group_banner_mobile_file: groupMobileFile,
          group_banner_mobile_url: groupMobileUrl,
          group_banner_web_file: groupWebFile,
          group_banner_web_url: groupWebUrl,
          // Preserve existing DB id so backend does UPDATE not INSERT
          ...(capturedEditGroupIndex !== -1 &&
          groups[capturedEditGroupIndex]?.id
            ? { id: groups[capturedEditGroupIndex].id }
            : {}),
        };

        if (capturedEditGroupIndex === -1) {
          return { ...tab, groups: [...groups, payload] };
        } else {
          return {
            ...tab,
            groups: groups.map((g, i) =>
              i === capturedEditGroupIndex ? payload : g,
            ),
          };
        }
      }),
    );

    if (capturedEditGroupIndex === -1) {
      setActiveGroupIndex(
        videoTabs[capturedActiveTabIndex]?.groups?.length || 0,
      );
    }

    resetGroupForm();
  };

  const handleEditGroup = (index) => {
    const group = videoTabs[activeTabIndex]?.groups?.[index];
    if (!group) return;
    setCurrentGroup({
      ...group,
      group_title: group.group_title,
      group_description: group.group_description || "",
      sort_order: group.sort_order || index,
    });
    setEditGroupIndex(index);
  };

  const handleDeleteGroup = (index) => {
    confirmAlert({
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this group? All videos in this group will be deleted.",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            setVideoTabs((prev) =>
              prev.map((tab, tabIdx) => {
                if (tabIdx !== activeTabIndex) return tab;
                const groups = (tab.groups || []).filter((_, i) => i !== index);
                return {
                  ...tab,
                  groups: groups.map((g, i) => ({ ...g, sort_order: i })),
                };
              }),
            );
            const currentGroups = videoTabs[activeTabIndex]?.groups || [];
            if (activeGroupIndex >= currentGroups.length - 1) {
              setActiveGroupIndex(Math.max(0, currentGroups.length - 2));
            }
            resetVideoForm();
            toast.success("Group deleted");
          },
        },
        { label: "No" },
      ],
    });
  };

  const handleMoveGroupUp = (index) => {
    if (index === 0) return;
    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== activeTabIndex) return tab;
        const groups = [...(tab.groups || [])];
        [groups[index - 1], groups[index]] = [groups[index], groups[index - 1]];
        return {
          ...tab,
          groups: groups.map((g, i) => ({ ...g, sort_order: i })),
        };
      }),
    );
  };

  const handleMoveGroupDown = (index) => {
    const currentGroups = videoTabs[activeTabIndex]?.groups || [];
    if (index === currentGroups.length - 1) return;
    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== activeTabIndex) return tab;
        const groups = [...(tab.groups || [])];
        [groups[index], groups[index + 1]] = [groups[index + 1], groups[index]];
        return {
          ...tab,
          groups: groups.map((g, i) => ({ ...g, sort_order: i })),
        };
      }),
    );
  };

  // ─── VIDEO MANAGEMENT ────────────────────────────────────────────────────

  const handleVideoCoverUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCurrentVideo((prev) => ({
      ...prev,
      cover_image_file: file,
      cover_image: URL.createObjectURL(file),
      delete_cover_image: false,
    }));
  };

  const handleAddOrUpdateVideo = () => {
    if (!currentVideo.url.trim()) {
      toast.error("Please enter a video URL");
      return;
    }
    if (videoTabs.length === 0) {
      toast.error("Please create a tab first");
      return;
    }

    const currentGroups = videoTabs[activeTabIndex]?.groups || [];
    if (currentGroups.length === 0) {
      toast.error("Please create a group first");
      return;
    }

    const membershipIds = currentVideo.membership_ids || [];
    if (!isVideoFree && membershipIds.length === 0) {
      toast.error("Please select membership for paid video");
      return;
    }

    let detectedType = "other";
    const url = currentVideo.url.trim();
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      detectedType = "youtube";
    } else if (url.includes("vimeo.com")) {
      detectedType = "vimeo";
    }

    let coverImageIndex = currentVideo.cover_image_index;
    let coverImage = currentVideo.cover_image;
    if (currentVideo.cover_image_file) {
      coverImageIndex = videoCoverFiles.length;
      setVideoCoverFiles((prev) => [...prev, currentVideo.cover_image_file]);
    }

    let attachFileUrl = null;
    let attachedFile = null;
    if (editVideoIndex === -1) {
      attachedFile = currentVideo.attachment_file || null;
    } else {
      if (currentVideo.attachment_file) {
        attachedFile = currentVideo.attachment_file;
      } else {
        attachFileUrl = currentVideo.attachment_file_url || null;
      }
    }

    const currentVideos =
      videoTabs[activeTabIndex]?.groups?.[activeGroupIndex]?.videos || [];

    const membershipExpiryPayload = buildMembershipExpiryPayload(
      isVideoFree,
      membershipIds,
      currentVideo.membership_expiry || {},
    );

    const payload = {
      id: currentVideo.id ?? null,
      title: currentVideo.title?.trim() || null,
      url: url,
      video_type: detectedType,
      membership_ids: isVideoFree ? [] : membershipIds,
      sort_order:
        editVideoIndex === -1 ? currentVideos.length : currentVideo.sort_order,
      cover_image: coverImage,
      cover_image_index: coverImageIndex,
      membership_expiry: membershipExpiryPayload,
      attachment_file: attachedFile,
      attachment_file_url: attachFileUrl,
      ...(currentVideo.delete_cover_image === true && !currentVideo.cover_image_file
        ? { delete_cover_image: true }
        : {}),
    };

    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== activeTabIndex) return tab;
        return {
          ...tab,
          groups: (tab.groups || []).map((group, groupIdx) => {
            if (groupIdx !== activeGroupIndex) return group;
            const videos = group.videos || [];
            if (editVideoIndex === -1) {
              return { ...group, videos: [...videos, payload] };
            } else {
              return {
                ...group,
                videos: videos.map((v, i) =>
                  i === editVideoIndex ? payload : v,
                ),
              };
            }
          }),
        };
      }),
    );

    resetVideoForm();
  };

  const handleEditVideo = (index) => {
    const video =
      videoTabs[activeTabIndex]?.groups?.[activeGroupIndex]?.videos?.[index];
    if (!video) return;

    const expiryMap = {};
    (video.membership_expiry || []).forEach((entry) => {
      expiryMap[entry.membership_id] = entry.expires_at;
    });

    setCurrentVideo({
      ...video,
      membership_ids: video.membership_ids || [],
      sort_order: video.sort_order || index,
      cover_image_file: null,
      membership_expiry: expiryMap,
    });
    setIsVideoFree((video.membership_ids || []).length === 0);
    setEditVideoIndex(index);
  };

  const handleDeleteVideo = (index) => {
    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== activeTabIndex) return tab;
        return {
          ...tab,
          groups: (tab.groups || []).map((group, groupIdx) => {
            if (groupIdx !== activeGroupIndex) return group;
            const videos = (group.videos || []).filter((_, i) => i !== index);
            return {
              ...group,
              videos: videos.map((v, i) => ({ ...v, sort_order: i })),
            };
          }),
        };
      }),
    );
  };

  const handleMoveVideoUp = (index) => {
    if (index === 0) return;
    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== activeTabIndex) return tab;
        return {
          ...tab,
          groups: (tab.groups || []).map((group, groupIdx) => {
            if (groupIdx !== activeGroupIndex) return group;
            const videos = [...(group.videos || [])];
            [videos[index - 1], videos[index]] = [
              videos[index],
              videos[index - 1],
            ];
            return {
              ...group,
              videos: videos.map((v, i) => ({ ...v, sort_order: i })),
            };
          }),
        };
      }),
    );
  };

  const handleMoveVideoDown = (index) => {
    const currentVideos =
      videoTabs[activeTabIndex]?.groups?.[activeGroupIndex]?.videos || [];
    if (index === currentVideos.length - 1) return;
    setVideoTabs((prev) =>
      prev.map((tab, tabIdx) => {
        if (tabIdx !== activeTabIndex) return tab;
        return {
          ...tab,
          groups: (tab.groups || []).map((group, groupIdx) => {
            if (groupIdx !== activeGroupIndex) return group;
            const videos = [...(group.videos || [])];
            [videos[index], videos[index + 1]] = [
              videos[index + 1],
              videos[index],
            ];
            return {
              ...group,
              videos: videos.map((v, i) => ({ ...v, sort_order: i })),
            };
          }),
        };
      }),
    );
  };

  const handleMembershipChange = (membership) => {
    const currentIds = currentVideo.membership_ids || [];
    const exists = currentIds.some(
      (id) => Number(id) === Number(membership.id),
    );
    let ids;
    if (exists) {
      ids = currentIds.filter((id) => Number(id) !== Number(membership.id));
      clearMembershipExpiry(membership.id);
    } else {
      ids = [...currentIds, Number(membership.id)];
    }
    setCurrentVideo((prev) => ({ ...prev, membership_ids: ids }));
  };

  // Reset group form when switching tabs
  useEffect(() => {
    resetGroupForm();
    resetVideoForm();
    setActiveGroupIndex(0);
  }, [activeTabIndex]);

  // Reset video form when switching groups
  useEffect(() => {
    resetVideoForm();
  }, [activeGroupIndex]);

  // Clear tabs when community changes to null
  useEffect(() => {
    if (editCommunityId === null) {
      setVideoTabs([]);
      setVideoCoverFiles([]);
    }
  }, [editCommunityId]);

  const currentTabData = videoTabs[activeTabIndex];
  const currentGroups = currentTabData?.groups || [];
  const currentGroupData = currentGroups[activeGroupIndex];
  const currentVideos = currentGroupData?.videos || [];

  return {
    videoTabs,
    setVideoTabs,
    activeTabIndex,
    setActiveTabIndex,
    activeGroupIndex,
    setActiveGroupIndex,
    videoCoverFiles,
    setVideoCoverFiles,
    currentTab,
    setCurrentTab,
    editTabIndex,
    currentGroup,
    setCurrentGroup,
    editGroupIndex,
    currentVideo,
    setCurrentVideo,
    editVideoIndex,
    isVideoFree,
    setIsVideoFree,
    currentTabData,
    currentGroups,
    currentGroupData,
    currentVideos,
    resetTabForm,
    resetGroupForm,
    resetVideoForm,
    setMembershipExpiry,
    clearMembershipExpiry,
    handleAddOrUpdateTab,
    handleEditTab,
    handleDeleteTab,
    handleMoveTabUp,
    handleMoveTabDown,
    handleGroupBannerWebUpload,
    handleGroupBannerMobileUpload,
    handleAddOrUpdateGroup,
    handleEditGroup,
    handleDeleteGroup,
    handleMoveGroupUp,
    handleMoveGroupDown,
    handleVideoCoverUpload,
    handleAddOrUpdateVideo,
    handleEditVideo,
    handleDeleteVideo,
    handleMoveVideoUp,
    handleMoveVideoDown,
    handleMembershipChange,
  };
};
